import type {TBBox} from '@postermywall/fabricjs-2';
import type {
  CanvasBounds,
  EqualSpacingInput,
  EqualSpacingResult,
  LengthFormatter,
  Measurement,
  MoveSnapInput,
  MoveSnapResult,
  SnapAxis,
  SnapBox,
  SnapGuide,
  SnapLine,
  SnapPoint,
} from './alignment-guides.types';
import {EQUAL_GAP_TOLERANCE, SNAP_LINE_TOLERANCE, SnapLineStyle} from './alignment-guides.types';

/**
 * Pure snapping geometry for the poster editor's alignment guides. Everything here works in the scene
 * (poster) plane and knows nothing about fabric objects or the DOM; the engine feeds it points and boxes
 * and applies the deltas it returns.
 */

/**
 * Where the moving target should land so that one of its reference points meets the nearest item point or
 * guide on each axis. The axes are independent (each only reads its own coordinate), so both deltas come
 * from the un-snapped origins and the lines are built from the fully snapped ones.
 */
export const resolveMoveSnap = (input: MoveSnapInput): MoveSnapResult => {
  const deltaX = resolveAxisDelta(input, 'x');
  const deltaY = resolveAxisDelta(input, 'y');
  const snappedOrigins = input.origins.map((origin) => ({x: origin.x + deltaX, y: origin.y + deltaY}));
  const lines = [...collectAxisLines({...input, origins: snappedOrigins}, 'x'), ...collectAxisLines({...input, origins: snappedOrigins}, 'y')];
  return {deltaX, deltaY, lines: mergeCollinearLines(lines)};
};

export const canvasBoundsToGuides = (bounds: CanvasBounds): SnapGuide[] => {
  const {left, top, width, height} = bounds;
  const right = left + width;
  const bottom = top + height;
  return [
    {axis: 'x', value: left, from: top, to: bottom},
    {axis: 'x', value: right, from: top, to: bottom},
    {axis: 'x', value: left + width / 2, from: top, to: bottom},
    {axis: 'y', value: top, from: left, to: right},
    {axis: 'y', value: bottom, from: left, to: right},
    {axis: 'y', value: top + height / 2, from: left, to: right},
  ];
};

/** One guide per grid line in each direction, from the design's top-left out to its far edges. */
export const gridToGuides = (bounds: CanvasBounds, spacing: number): SnapGuide[] => {
  if (!Number.isFinite(spacing) || spacing <= 0) {
    return [];
  }
  const right = bounds.left + bounds.width;
  const bottom = bounds.top + bounds.height;
  const guides: SnapGuide[] = [];
  for (let x = bounds.left; x <= right; x += spacing) {
    guides.push({axis: 'x', value: x, from: bounds.top, to: bottom});
  }
  for (let y = bounds.top; y <= bottom; y += spacing) {
    guides.push({axis: 'y', value: y, from: bounds.left, to: right});
  }
  return guides;
};

/**
 * Equal-spacing snapping along one axis. Neighbours that share the target's lane (its row for `x`, its
 * column for `y`) and sit clear of it on that axis can form a gap with it; the target snaps to where its
 * gap to the nearest neighbour equals an existing gap — between two neighbours on one side, or on both
 * sides when it sits between two of them. Returns null when no such position is within the margin.
 */
export const resolveEqualSpacing = (input: EqualSpacingInput): EqualSpacingResult | null => {
  const {target, axis, margin, locked, formatLength} = input;
  const [targetStart, targetEnd] = along(target, axis);
  const neighbours = getLaneNeighbours(input);
  // Each side is ordered by the edge that faces the target, so the last `before` and first `after` are the nearest.
  const before = neighbours.filter((neighbour) => along(neighbour, axis)[1] <= targetStart).sort((a, b) => along(a, axis)[1] - along(b, axis)[1]);
  const after = neighbours.filter((neighbour) => along(neighbour, axis)[0] >= targetEnd).sort((a, b) => along(a, axis)[0] - along(b, axis)[0]);

  const candidates = [
    getBetweenCandidate(target, before, after, axis),
    getAfterPairCandidate(target, before, axis),
    getBeforePairCandidate(target, after, axis),
  ].filter((candidate): candidate is SpacingCandidate => candidate !== null && Math.abs(candidate.delta) <= margin);

  const best = candidates.sort((a, b) => Math.abs(a.delta) - Math.abs(b.delta))[0];
  if (!best) {
    return null;
  }
  if (locked && Math.abs(best.delta) > EQUAL_GAP_TOLERANCE) {
    return null;
  }

  const delta = locked ? 0 : best.delta;
  const at = getMeasurementPosition(target, best.neighbours, axis);
  const label = formatLength(best.gap);
  return {delta, measurements: best.gaps.map(([from, to]) => ({axis, from, to, at, label}))};
};

/**
 * Where the gap lines are drawn across the axis: the middle of the span the neighbours have in common, so the
 * pills sit between the items whose gap they label and stay put while the target is dragged along its lane.
 * Neighbours that do not overlap each other fall back to the target's middle.
 */
const getMeasurementPosition = (target: SnapBox, neighbours: SnapBox[], axis: SnapAxis): number => {
  const spans = neighbours.map((neighbour) => across(neighbour, axis));
  const start = Math.max(...spans.map(([spanStart]) => spanStart));
  const end = Math.min(...spans.map(([, spanEnd]) => spanEnd));
  if (start < end) {
    return (start + end) / 2;
  }
  const [targetStart, targetEnd] = across(target, axis);
  return (targetStart + targetEnd) / 2;
};

/** The target's distance to each edge of the design area, as labelled gaps. Gaps under a pixel are left out. */
export const measureToBounds = (target: SnapBox, bounds: CanvasBounds, formatLength: LengthFormatter): Measurement[] => {
  const boundsRight = bounds.left + bounds.width;
  const boundsBottom = bounds.top + bounds.height;
  const centerX = (target.left + target.right) / 2;
  const centerY = (target.top + target.bottom) / 2;
  const gaps: Omit<Measurement, 'label'>[] = [
    {axis: 'x', from: bounds.left, to: target.left, at: centerY},
    {axis: 'x', from: target.right, to: boundsRight, at: centerY},
    {axis: 'y', from: bounds.top, to: target.top, at: centerX},
    {axis: 'y', from: target.bottom, to: boundsBottom, at: centerX},
  ];
  return gaps.filter((gap) => Math.round(gap.to - gap.from) > 0).map((gap) => ({...gap, label: formatLength(gap.to - gap.from)}));
};

export const toSnapBox = (rect: TBBox): SnapBox => ({
  left: rect.left,
  top: rect.top,
  right: rect.left + rect.width,
  bottom: rect.top + rect.height,
});

interface NearestOnAxis {
  distance: number;
  value: number;
}

/** The nearest item point or guide to `origin` along one axis; the two kinds compete on equal terms. */
const nearestOnAxis = (origin: SnapPoint, candidates: SnapPoint[], guides: SnapGuide[], axis: SnapAxis): NearestOnAxis => {
  const values = [...candidates.map((candidate) => candidate[axis]), ...guides.filter((guide) => guide.axis === axis).map((guide) => guide.value)];
  let nearest: NearestOnAxis = {distance: Infinity, value: origin[axis]};
  for (const value of values) {
    const distance = Math.abs(origin[axis] - value);
    if (distance < nearest.distance) {
      nearest = {distance, value};
    }
  }
  return nearest;
};

const resolveAxisDelta = (input: MoveSnapInput, axis: SnapAxis): number => {
  const matches = input.origins.map((origin) => nearestOnAxis(origin, input.candidates, input.guides, axis));
  const minDistance = Math.min(...matches.map((match) => match.distance));
  if (minDistance > input.margin) {
    return 0;
  }
  const index = matches.findIndex((match) => match.distance === minDistance);
  return matches[index].value - input.origins[index][axis];
};

/**
 * Lines for every origin that now sits on a candidate along `axis`; call with the snapped origins. Corners
 * come out of the object's transform matrix, so a snapped right or bottom edge can miss the value it was
 * snapped to by a rounding error; anything within {@link SNAP_LINE_TOLERANCE} counts as on it.
 */
const collectAxisLines = (input: MoveSnapInput, axis: SnapAxis): SnapLine[] => {
  const other: SnapAxis = axis === 'x' ? 'y' : 'x';
  const lines: SnapLine[] = [];
  for (const origin of input.origins) {
    const isOnOrigin = (value: number): boolean => Math.abs(origin[axis] - value) <= SNAP_LINE_TOLERANCE;
    for (const point of input.candidates.filter((candidate) => isOnOrigin(candidate[axis]))) {
      lines.push({axis, value: point[axis], from: Math.min(origin[other], point[other]), to: Math.max(origin[other], point[other]), style: SnapLineStyle.OBJECT});
    }
    for (const guide of input.guides.filter((candidate) => candidate.axis === axis && isOnOrigin(candidate.value))) {
      lines.push({axis, value: guide.value, from: guide.from, to: guide.to, style: SnapLineStyle.GUIDE});
    }
  }
  return lines;
};

/** One line per (axis, value, style), spanning every matched point, so a column of aligned items reads as a single guide. */
const mergeCollinearLines = (lines: SnapLine[]): SnapLine[] => {
  const merged = new Map<string, SnapLine>();
  for (const line of lines) {
    const key = `${line.axis}:${line.value}:${line.style}`;
    const existing = merged.get(key);
    if (existing) {
      existing.from = Math.min(existing.from, line.from);
      existing.to = Math.max(existing.to, line.to);
    } else {
      merged.set(key, {...line});
    }
  }
  return Array.from(merged.values());
};

interface SpacingCandidate {
  /** Translation along the axis that produces the equal gaps. */
  delta: number;
  gap: number;
  /** The gaps as [from, to] along the axis, at the snapped position. */
  gaps: [number, number][];
  /** The neighbours that bound the gaps. */
  neighbours: SnapBox[];
}

/** Start and end of a box along the axis being spaced. */
const along = (snapBox: SnapBox, axis: SnapAxis): [number, number] => (axis === 'x' ? [snapBox.left, snapBox.right] : [snapBox.top, snapBox.bottom]);

/** Start and end of a box along the perpendicular axis. */
const across = (snapBox: SnapBox, axis: SnapAxis): [number, number] => (axis === 'x' ? [snapBox.top, snapBox.bottom] : [snapBox.left, snapBox.right]);

/** Neighbours in the target's lane that do not overlap it along the axis — the only ones that can form a gap with it. */
const getLaneNeighbours = ({target, neighbours, axis}: EqualSpacingInput): SnapBox[] => {
  const [targetStart, targetEnd] = along(target, axis);
  const [targetAcrossStart, targetAcrossEnd] = across(target, axis);
  return neighbours.filter((neighbour) => {
    const [start, end] = along(neighbour, axis);
    const [acrossStart, acrossEnd] = across(neighbour, axis);
    const sharesLane = acrossStart < targetAcrossEnd && acrossEnd > targetAcrossStart;
    const overlapsTarget = end > targetStart && start < targetEnd;
    return sharesLane && !overlapsTarget;
  });
};

const getBetweenCandidate = (target: SnapBox, before: SnapBox[], after: SnapBox[], axis: SnapAxis): SpacingCandidate | null => {
  if (before.length === 0 || after.length === 0) {
    return null;
  }
  const [targetStart, targetEnd] = along(target, axis);
  const size = targetEnd - targetStart;
  const previous = before[before.length - 1];
  const next = after[0];
  const previousEnd = along(previous, axis)[1];
  const nextStart = along(next, axis)[0];
  const gap = (nextStart - previousEnd - size) / 2;
  if (gap <= 0) {
    return null;
  }
  const start = previousEnd + gap;
  return {
    delta: start - targetStart,
    gap,
    gaps: [
      [previousEnd, start],
      [start + size, nextStart],
    ],
    neighbours: [previous, next],
  };
};

/** The target's gap to the nearest neighbour before it equals that neighbour's gap to the one before it. */
const getAfterPairCandidate = (target: SnapBox, before: SnapBox[], axis: SnapAxis): SpacingCandidate | null => {
  const pair = getNearestPair(before, axis, 'end');
  if (!pair) {
    return null;
  }
  const [first, second] = pair;
  const gap = along(second, axis)[0] - along(first, axis)[1];
  const start = along(second, axis)[1] + gap;
  return {
    delta: start - along(target, axis)[0],
    gap,
    gaps: [
      [along(first, axis)[1], along(second, axis)[0]],
      [along(second, axis)[1], start],
    ],
    neighbours: [first, second],
  };
};

/** Mirror of {@link getAfterPairCandidate}: the target sits before a pair of neighbours. */
const getBeforePairCandidate = (target: SnapBox, after: SnapBox[], axis: SnapAxis): SpacingCandidate | null => {
  const pair = getNearestPair(after, axis, 'start');
  if (!pair) {
    return null;
  }
  const [first, second] = pair;
  const gap = along(second, axis)[0] - along(first, axis)[1];
  const end = along(first, axis)[0] - gap;
  return {
    delta: end - along(target, axis)[1],
    gap,
    gaps: [
      [end, along(first, axis)[0]],
      [along(first, axis)[1], along(second, axis)[0]],
    ],
    neighbours: [first, second],
  };
};

/**
 * The two neighbours nearest the target on one side, in axis order, provided a positive gap separates them.
 * `nearestEdge` says which end of the (axis-sorted) list is nearest the target.
 */
const getNearestPair = (side: SnapBox[], axis: SnapAxis, nearestEdge: 'start' | 'end'): [SnapBox, SnapBox] | null => {
  if (side.length < 2) {
    return null;
  }
  const ordered = nearestEdge === 'end' ? [...side].reverse() : side;
  const nearest = ordered[0];
  for (const other of ordered.slice(1)) {
    const [first, second] = nearestEdge === 'end' ? [other, nearest] : [nearest, other];
    if (along(second, axis)[0] > along(first, axis)[1]) {
      return [first, second];
    }
  }
  return null;
};
